import React from 'react';
import { useLocation } from 'react-router-dom';

const siteUrl = 'https://prismaops.co.za';

const pageMeta: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'PrismaOps | AI, Automation & Software Systems',
    description: 'PrismaOps designs practical AI, automation, and software systems that make business operations easier to run.',
  },
  '/services': {
    title: 'Services & Solutions | PrismaOps',
    description: 'Explore PrismaOps automation, internal tool, and systems audit services for growing businesses.',
  },
  '/portfolio': {
    title: 'Work & Portfolio | PrismaOps',
    description: 'Explore PrismaOps examples of dashboards, workflow systems, trackers, and digital products.',
  },
  '/sample': {
    title: 'Sample Builds | PrismaOps',
    description: 'Review sample internal tools, collaboration systems, booking flows, and workflow builds from PrismaOps.',
  },
  '/github-training': {
    title: 'GitHub Training & Support | PrismaOps',
    description: 'Practical GitHub training, project guidance, and technical support from PrismaOps.',
  },
  '/contact': {
    title: 'Contact PrismaOps | Start a Systems Conversation',
    description: 'Talk to PrismaOps about the workflow, software, or automation system your business needs.',
  },
  '/web-ui-design': {
    title: 'Web & UI Design | PrismaOps',
    description: 'Modern, responsive web and interface design for products, teams, and growing businesses.',
  },
  '/graphic-design': {
    title: 'Graphic Design | PrismaOps',
    description: 'Brand, presentation, social, and digital design services from PrismaOps.',
  },
};

const setMeta = (name: string, content: string) => {
  let element = document.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const setProperty = (property: string, content: string) => {
  let element = document.querySelector(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

export const Seo: React.FC = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    const basePath = pathname.startsWith('/sample/') ? '/sample' : pathname;
    const metadata = pageMeta[basePath] ?? pageMeta['/'];
    const canonicalUrl = `${siteUrl}${pathname === '/' ? '/' : pathname}`;

    document.title = metadata.title;
    setMeta('description', metadata.description);
    setMeta('robots', 'index, follow');
    setProperty('og:title', metadata.title);
    setProperty('og:description', metadata.description);
    setProperty('og:url', canonicalUrl);
    setProperty('og:type', 'website');
    setMeta('twitter:title', metadata.title);
    setMeta('twitter:description', metadata.description);

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    let structuredData = document.querySelector<HTMLScriptElement>('#prismaops-structured-data');
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.id = 'prismaops-structured-data';
      structuredData.type = 'application/ld+json';
      document.head.appendChild(structuredData);
    }
    structuredData.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      name: 'PrismaOps',
      url: siteUrl,
      logo: `${siteUrl}/logo.jpeg`,
      email: 'Admin@prismaops.co.za',
      areaServed: 'South Africa',
      description: metadata.description,
      serviceType: ['AI systems', 'Business automation', 'Internal software tools', 'Systems audits'],
    });
  }, [pathname]);

  return null;
};
